/**
 * The app's ops-look vocabulary — complete literal Tailwind class strings,
 * copied verbatim from /ops so every surface restyled to match it (planner
 * first, staff app phase by phase) pulls from one place instead of drifting.
 *
 * Rules of use:
 *   - Append extra classes (`${CARD} mt-3`) — never build class fragments,
 *     Tailwind's scanner only sees complete literals.
 *   - Don't add a conflicting utility (e.g. another rounded-* on BTN_PRIMARY);
 *     write the variant out in full instead.
 *   - active:, not hover: — this app is touch-first.
 */

// Page + chrome planes
export const PAGE_BG = 'min-h-[100dvh] bg-gradient-to-br from-[#d13342] to-[#8c232d]';
export const CHROME = 'sticky top-0 z-10 bg-[#9a2935] shadow-[0_1px_0_0_rgba(255,255,255,0.08)]';
export const POPOVER_BG = 'bg-[#7d2029]';

// Surfaces
export const CARD = 'rounded-2xl px-4 py-3.5 bg-white/[0.07] ring-1 ring-white/15';
export const CARD_TAP = 'active:bg-white/[0.12] touch-manipulation transition-colors';
export const HERO = 'bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-2xl';
// A hairline box, no fill (owner 2026-10-02). What a guest reads on the public
// form and the place page sits in it: the arrive-by and the wait, a question
// before a tap. HERO's glass read as a shaded slab there, CARD's 7% fill as
// low contrast.
export const BOX_LINE = 'rounded-2xl ring-1 ring-white/30';

// Buttons
export const BTN_PRIMARY = 'min-h-[44px] rounded-xl bg-white px-4 font-semibold text-red-700 active:bg-white/90 touch-manipulation disabled:opacity-50';
export const BTN_SECONDARY = 'min-h-[44px] rounded-xl bg-white/10 border border-white/20 px-4 font-medium text-white/80 active:bg-white/15 touch-manipulation disabled:opacity-40';

// Inputs — INPUT_WHITE is the on-gradient default: native date/time/select
// pickers render light (globals.css locks color-scheme to light), so white
// fields keep them looking correct. INPUT_GLASS is for inside sheets.
export const INPUT_WHITE = 'rounded-xl border border-red-700/30 bg-white px-3 py-2.5 text-red-800 placeholder:text-red-700/40 focus:outline-none focus:ring-2 focus:ring-white/60';
export const INPUT_GLASS = 'rounded-xl bg-white/10 border-2 border-white/30 px-3 py-2.5 text-white placeholder:text-white/40 focus:outline-none focus:border-white/60';

// Chips — the /ops three-tier system (ThreadCard) plus a success tone
export const CHIP = 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1';
export const CHIP_ACT = 'bg-white text-gray-900 ring-white';
export const CHIP_ATTENTION = 'bg-white/45 text-white ring-white/60';
export const CHIP_QUIET = 'bg-white/10 text-white/70 ring-white/25';
export const CHIP_SUCCESS = 'bg-emerald-400/20 text-emerald-100 ring-emerald-300/40';

// A chip you can TAP. Spelled out rather than CHIP plus overrides, because
// px-2.5 and CHIP's px-2 would race on source order and the winner would
// depend on how Tailwind happened to sort them that build.
//
// before:-inset-1.5 grows the touch target past the visual pill to reach 44px
// without changing layout — the trick BottomSheet's close button already uses.
export const CHIP_BTN = 'relative inline-flex items-center gap-1.5 rounded-full px-2.5 min-h-[32px] text-[11px] font-bold tracking-wide ring-1 active:opacity-75 touch-manipulation before:absolute before:-inset-1.5 before:content-[""]';

// A DERIVED fact, not a state anybody chose: "we replied", "wrote back".
// Transparent so the one filled chip in a row is unmistakably the one you can
// act on. Same treatment ThreadCard gives its location tag.
export const CHIP_FACT = 'bg-transparent text-white/50 ring-white/20';

// Blocks an action. Amber, never red — red text is invisible on this gradient,
// and amber already means "you cannot send" everywhere else in the app.
export const CHIP_WARN = 'bg-amber-400/20 text-amber-100 ring-amber-300/40';

// The empty state, on the detail page only. The list correctly hides `new`
// across 773 people as wallpaper; on one person's page the ABSENCE of a
// decision is the thing you opened the page to fix, so it gets a control.
export const CHIP_EMPTY = 'relative inline-flex items-center gap-1.5 rounded-full px-2.5 min-h-[32px] text-[11px] font-semibold ring-1 ring-dashed ring-white/40 text-white/70 active:bg-white/10 touch-manipulation before:absolute before:-inset-1.5 before:content-[""]';

// Banners — emerald for success; amber for errors (red text is invisible on
// the red gradient, and amber is the /ops attention accent)
export const BANNER_SUCCESS = 'rounded-xl bg-emerald-400/15 ring-1 ring-emerald-300/30 px-4 py-3 text-sm text-emerald-100';
export const BANNER_ERROR = 'rounded-xl bg-amber-400/15 ring-1 ring-amber-300/40 px-4 py-3 text-sm text-amber-100';

// The small uppercase heading over a group of controls or a list section.
// Written out verbatim about a dozen times across the hiring pages and present
// in this file zero times, which is exactly backwards.
export const LABEL = 'text-[9.5px] font-bold tracking-[0.08em] uppercase text-white/50';

// A FILTER chip, the kind you toggle on and off.
//
// Both hiring pages hand-rolled an identical `chip(on)` helper, and both built
// it at min-h-[34px] — under the 44px minimum, for every filter, on the two
// pages most made of filters. Fixed the way CHIP_BTN already does it:
// before:-inset-1.5 grows the touch target past the visual pill without
// growing the pill, so nothing about the layout moves.
//
// Two complete literals rather than a base plus overrides, because the on and
// off states differ in background AND text colour, and composing those would
// race on Tailwind's source order.
export const FILTER_CHIP_ON = 'relative px-3 py-1.5 rounded-full text-[12px] font-semibold bg-white text-[#8c232d] touch-manipulation before:absolute before:-inset-1.5 before:content-[""]';
export const FILTER_CHIP_OFF = 'relative px-3 py-1.5 rounded-full text-[12px] font-semibold bg-white/[0.13] text-white/80 active:bg-white/20 touch-manipulation before:absolute before:-inset-1.5 before:content-[""]';

// An OPAQUE plane, for anything you have to read carefully before an
// irreversible tap.
//
// Deliberately not glass. ReplyButtons found this the hard way: the page
// scrolling behind a translucent bar showed straight through the words, and
// those words were the message about to be sent to a stranger. Anything with a
// send button on it uses this.
export const PLANE_OPAQUE = 'bg-[#6d1a22] ring-1 ring-white/25 rounded-xl shadow-[0_-8px_24px_rgba(0,0,0,.35)]';

// A SEGMENTED filter control: one question, its options joined into one pill.
//
// Six chips in a row answering three different questions - role, location,
// hours - is six chips in a row. "Front of House" and "Full-Time" both lit,
// nothing saying they belong to different questions, and the reader has to know
// the domain to parse the row at all. Joining each question's options into one
// track says "these three belong together and those do not" before a single
// word is read.
//
// STILL MULTI-SELECT, which is the one thing a segmented control usually is
// not. The 3px gutter between segments is what carries that: two lit segments
// read as two lit segments rather than as one wide selection, so "both roles"
// is a state you can see. Nothing lit means the question is not being asked.
//
// 38px segment inside 3px of padding is a 44px control, and before:-inset-y
// grows each segment's own target to the full 44 - the trick CHIP_BTN and the
// filter chips already use, so the visual pill and the touch target can differ.
export const SEGMENT_GROUP = 'inline-flex items-center gap-[3px] rounded-full bg-white/[0.09] ring-1 ring-white/20 p-[3px]';
export const SEGMENT_ON = 'relative inline-flex items-center justify-center px-3.5 min-h-[38px] rounded-full text-[12px] font-semibold bg-white text-[#8c232d] touch-manipulation before:absolute before:-inset-y-[3px] before:inset-x-0 before:content-[""]';
export const SEGMENT_OFF = 'relative inline-flex items-center justify-center px-3.5 min-h-[38px] rounded-full text-[12px] font-semibold text-white/75 active:bg-white/15 touch-manipulation before:absolute before:-inset-y-[3px] before:inset-x-0 before:content-[""]';

// A segmented NUMBER picker in SEGMENT_GROUP: single-select, each segment a
// full 44px tap with a big digit ("How many stools opened side by side?
// 1 2 3 4", owner board 2026-10-03). Written out in full rather than SEGMENT_ON
// plus overrides, for the source-order race described at the top.
export const SEGMENT_NUM_ON = 'relative inline-flex items-center justify-center min-w-[52px] px-3.5 min-h-[44px] rounded-full text-[15px] font-bold bg-white text-[#8c232d] touch-manipulation disabled:opacity-40';
export const SEGMENT_NUM_OFF = 'relative inline-flex items-center justify-center min-w-[52px] px-3.5 min-h-[44px] rounded-full text-[15px] font-bold text-white/75 active:bg-white/15 touch-manipulation disabled:opacity-40';
